import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { 
  FiMail, FiSend, FiBell, FiMessageCircle, FiUsers, FiPlus, FiSearch,
  FiFilter, FiEdit3, FiTrash2, FiClock, FiCheckCircle, FiAlertCircle,
  FiEye, FiX, FiPaperclip, FiCalendar, FiTrendingUp, FiActivity
} = FiIcons;

const CommunicationHub = () => {
  const [activeTab, setActiveTab] = useState('inbox');
  const [showComposeModal, setShowComposeModal] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  const communications = [
    {
      id: 1,
      type: 'email',
      subject: 'Welcome to the Platform',
      preview: 'Thank you for joining our dental referral network...',
      recipients: 234,
      status: 'sent',
      sentDate: '2024-01-20',
      openRate: 68.5,
      clickRate: 12.3
    },
    {
      id: 2,
      type: 'announcement',
      subject: 'New Features Released',
      preview: 'We\'re excited to announce several new features...',
      recipients: 1247,
      status: 'sent',
      sentDate: '2024-01-18',
      openRate: 72.1,
      clickRate: 18.7
    },
    {
      id: 3,
      type: 'notification',
      subject: 'System Maintenance Scheduled',
      preview: 'Please be aware that we will be performing...',
      recipients: 1247,
      status: 'scheduled',
      sentDate: '2024-01-25',
      openRate: 0,
      clickRate: 0
    },
    {
      id: 4,
      type: 'email',
      subject: 'Monthly Newsletter - January',
      preview: 'Here\'s your monthly roundup of platform updates...',
      recipients: 1156,
      status: 'draft',
      sentDate: null,
      openRate: 0,
      clickRate: 0
    }
  ];

  const templates = [
    { id: 1, name: 'Welcome Email', category: 'Onboarding', usage: 234 },
    { id: 2, name: 'Password Reset', category: 'Account', usage: 567 },
    { id: 3, name: 'Monthly Newsletter', category: 'Marketing', usage: 12 },
    { id: 4, name: 'System Alert', category: 'Technical', usage: 89 }
  ];

  const stats = {
    totalSent: 3456,
    avgOpenRate: 67.3,
    avgClickRate: 15.2,
    activeSubscribers: 1247
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'sent': return 'bg-green-100 text-green-800';
      case 'scheduled': return 'bg-blue-100 text-blue-800';
      case 'draft': return 'bg-gray-100 text-gray-800';
      case 'failed': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getTypeIcon = (type) => {
    switch (type) {
      case 'email': return FiMail;
      case 'announcement': return FiBell;
      case 'notification': return FiMessageCircle;
      default: return FiMail;
    }
  };

  const filteredCommunications = communications.filter(comm =>
    comm.subject.toLowerCase().includes(searchTerm.toLowerCase()) ||
    comm.preview.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-red-50 to-pink-50">
      <div className="max-w-7xl mx-auto p-6">
        {/* Header */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Communication Hub</h1>
              <p className="text-gray-600 mt-1">Manage all your communications in one place</p>
            </div>
            <div className="mt-4 sm:mt-0">
              <button 
                onClick={() => setShowComposeModal(true)}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors flex items-center space-x-2"
              >
                <SafeIcon icon={FiPlus} className="w-4 h-4" />
                <span>Compose</span>
              </button>
            </div>
          </div>
        </motion.div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="bg-white rounded-xl p-6 shadow-sm border border-gray-100"
          >
            <div className="flex items-center justify-between mb-4">
              <div className="p-3 bg-red-50 rounded-lg">
                <SafeIcon icon={FiSend} className="w-6 h-6 text-red-600" />
              </div>
              <span className="text-sm text-green-600 font-medium">+24%</span>
            </div>
            <p className="text-2xl font-bold text-gray-900">{stats.totalSent}</p>
            <p className="text-sm text-gray-600 mt-1">Total Sent</p>
          </motion.div>

          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="bg-white rounded-xl p-6 shadow-sm border border-gray-100"
          >
            <div className="flex items-center justify-between mb-4">
              <div className="p-3 bg-blue-50 rounded-lg">
                <SafeIcon icon={FiEye} className="w-6 h-6 text-blue-600" />
              </div>
              <span className="text-sm text-green-600 font-medium">+5.2%</span>
            </div>
            <p className="text-2xl font-bold text-gray-900">{stats.avgOpenRate}%</p>
            <p className="text-sm text-gray-600 mt-1">Avg Open Rate</p>
          </motion.div>

          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="bg-white rounded-xl p-6 shadow-sm border border-gray-100"
          >
            <div className="flex items-center justify-between mb-4">
              <div className="p-3 bg-purple-50 rounded-lg">
                <SafeIcon icon={FiTrendingUp} className="w-6 h-6 text-purple-600" />
              </div>
              <span className="text-sm text-green-600 font-medium">+3.1%</span>
            </div>
            <p className="text-2xl font-bold text-gray-900">{stats.avgClickRate}%</p>
            <p className="text-sm text-gray-600 mt-1">Avg Click Rate</p>
          </motion.div>

          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="bg-white rounded-xl p-6 shadow-sm border border-gray-100"
          >
            <div className="flex items-center justify-between mb-4">
              <div className="p-3 bg-green-50 rounded-lg">
                <SafeIcon icon={FiUsers} className="w-6 h-6 text-green-600" />
              </div>
              <span className="text-sm text-green-600 font-medium">+8%</span>
            </div>
            <p className="text-2xl font-bold text-gray-900">{stats.activeSubscribers}</p>
            <p className="text-sm text-gray-600 mt-1">Active Subscribers</p>
          </motion.div>
        </div>

        {/* Tabs */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
          className="bg-white rounded-xl shadow-sm border border-gray-100 mb-6"
        >
          <div className="flex space-x-8 p-6 border-b border-gray-100 overflow-x-auto">
            {[
              { id: 'inbox', label: 'Inbox', icon: FiMail },
              { id: 'sent', label: 'Sent', icon: FiSend },
              { id: 'scheduled', label: 'Scheduled', icon: FiClock },
              { id: 'templates', label: 'Templates', icon: FiEdit3 }
            ].map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center space-x-2 py-2 px-1 border-b-2 font-medium text-sm transition-colors whitespace-nowrap ${
                  activeTab === tab.id
                    ? 'border-red-500 text-red-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                <SafeIcon icon={tab.icon} className="w-4 h-4" />
                <span>{tab.label}</span>
              </button>
            ))}
          </div>
        </motion.div>

        {/* Search and Filters */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6 }}
          className="bg-white rounded-xl p-6 shadow-sm border border-gray-100 mb-6"
        >
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-4 sm:space-y-0">
            <div className="relative flex-1 max-w-md">
              <SafeIcon icon={FiSearch} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <input
                type="text"
                placeholder="Search communications..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
              />
            </div>
            <button className="px-4 py-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors flex items-center space-x-2">
              <SafeIcon icon={FiFilter} className="w-4 h-4" />
              <span>Filters</span>
            </button>
          </div>
        </motion.div>

        {/* Content */}
        <AnimatePresence mode="wait">
          {activeTab === 'templates' ? (
            <motion.div
              key="templates"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6"
            >
              {templates.map((template, index) => (
                <motion.div
                  key={template.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.7 + index * 0.1 }}
                  className="bg-white rounded-xl p-6 shadow-sm border border-gray-100 hover:shadow-md transition-all cursor-pointer"
                >
                  <div className="flex items-center justify-between mb-4">
                    <div className="p-3 bg-red-50 rounded-lg">
                      <SafeIcon icon={FiEdit3} className="w-6 h-6 text-red-600" />
                    </div>
                    <button className="text-gray-400 hover:text-gray-600">
                      <SafeIcon icon={FiTrash2} className="w-4 h-4" />
                    </button>
                  </div>
                  <h3 className="font-semibold text-gray-900 mb-2">{template.name}</h3>
                  <p className="text-sm text-gray-500 mb-4">{template.category}</p>
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-600">Used {template.usage} times</span>
                  </div>
                </motion.div>
              ))}
            </motion.div>
          ) : (
            <motion.div
              key="communications"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              className="bg-white rounded-xl shadow-sm border border-gray-100"
            >
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Subject
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Type
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Recipients
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Status
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Open Rate
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {filteredCommunications.map((comm) => (
                      <tr key={comm.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4">
                          <div>
                            <div className="text-sm font-medium text-gray-900">{comm.subject}</div>
                            <div className="text-sm text-gray-500">{comm.preview}</div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center space-x-2">
                            <SafeIcon icon={getTypeIcon(comm.type)} className="w-4 h-4 text-gray-400" />
                            <span className="text-sm text-gray-900 capitalize">{comm.type}</span>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className="text-sm text-gray-900">{comm.recipients}</span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${getStatusColor(comm.status)}`}>
                            {comm.status}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          {comm.status === 'sent' ? (
                            <div className="text-sm">
                              <div className="text-gray-900">{comm.openRate}%</div>
                              <div className="text-gray-500">{comm.clickRate}% clicks</div>
                            </div>
                          ) : (
                            <span className="text-sm text-gray-400">-</span>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                          <div className="flex items-center space-x-2">
                            <button className="text-blue-600 hover:text-blue-900">View</button>
                            <button className="text-gray-600 hover:text-gray-900">Edit</button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Compose Modal */}
        <AnimatePresence>
          {showComposeModal && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4"
              onClick={() => setShowComposeModal(false)}
            >
              <motion.div
                initial={{ scale: 0.9, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.9, opacity: 0 }}
                className="bg-white rounded-xl shadow-2xl w-full max-w-3xl max-h-[90vh] overflow-y-auto"
                onClick={e => e.stopPropagation()}
              >
                <div className="p-6 border-b border-gray-200 sticky top-0 bg-white z-10">
                  <div className="flex justify-between items-center">
                    <div>
                      <h3 className="text-xl font-bold text-gray-800">Compose Message</h3>
                      <p className="text-sm text-gray-500 mt-1">Create a new communication</p>
                    </div>
                    <button
                      onClick={() => setShowComposeModal(false)}
                      className="text-gray-400 hover:text-gray-600 transition-colors"
                    >
                      <SafeIcon icon={FiX} className="w-6 h-6" />
                    </button>
                  </div>
                </div>

                <div className="p-6 space-y-6">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Communication Type *
                    </label>
                    <select className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent">
                      <option value="email">Email</option>
                      <option value="announcement">Announcement</option>
                      <option value="notification">Notification</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Recipients *
                    </label>
                    <select className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent">
                      <option value="all">All Users</option>
                      <option value="providers">All Providers</option>
                      <option value="patients">All Patients</option>
                      <option value="custom">Custom List</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Subject *
                    </label>
                    <input
                      type="text"
                      placeholder="Enter subject line..."
                      className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Message *
                    </label>
                    <textarea
                      placeholder="Compose your message..."
                      rows={8}
                      className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Schedule (Optional)
                    </label>
                    <input
                      type="datetime-local"
                      className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                    />
                  </div>
                </div>

                <div className="p-4 bg-gray-50 border-t border-gray-200 flex justify-end gap-3 sticky bottom-0">
                  <button
                    onClick={() => setShowComposeModal(false)}
                    className="py-2.5 px-5 bg-white border border-gray-300 text-gray-700 font-semibold rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    Cancel
                  </button>
                  <button className="py-2.5 px-6 bg-gray-600 text-white font-semibold rounded-lg hover:bg-gray-700 transition-colors">
                    Save Draft
                  </button>
                  <button className="py-2.5 px-6 bg-red-600 text-white font-semibold rounded-lg hover:bg-red-700 transition-colors flex items-center space-x-2">
                    <SafeIcon icon={FiSend} className="w-4 h-4" />
                    <span>Send Now</span>
                  </button>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
};

export default CommunicationHub;